import { useCallback, useState } from "react";
import { Stack, Button, Link, Typography, Divider, FormControl } from "@mui/material";
import LogLayout from "../../../Layouts/Log";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { cacheService, userService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { routePaths } from "../../../Router/router";
import { useNavigate, useParams } from "react-router";
import FormInputPassword from "../../../Components/Forms/FormInputPassword/form-input-password";
import { useAppSelector } from "../../../Redux/redux-store";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { CheckPassword } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";

/*
Utility: Change password for a forgot password request.
*/

const LoginForgotPasswordReset = () => {
  const { t } = useTranslation();
  const params = useParams<{ key: string; userId: string }>();
  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const formId = `verify-user`;
  const formValid = useAppSelector(GetFormIsValid(formId));

  const onSubmit = useCallback(() => {
    let check = CheckPassword(pass1, pass2);

    if (!check.success) {
      enqueueCustomSnackbar(
        t("alert:error.newPasswordMustFollowInstructions", { message: check.errorMessages.join("\r\n- ") }),
        "warning",
        true
      );
      return false;
    }

    userService
      .ForgotPasswordReset({
        userId: params.userId ? parseInt(params.userId) : -1,
        key: params.key ?? "",
        password: pass1,
      })
      .then(function (response) {
        if (response.success && response.authKey) {
          navigate(routePaths.login.path + "/" + response.authKey);
          enqueueCustomSnackbar(t("alert:success.passwordHasBeenReset"), "success");
        } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
      });
  }, [params, pass1, pass2]);

  return (
    <LogLayout
      sxRightContainer={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
      catchSentence={cacheService.GetCatchPhrase()}
    >
      <Stack alignItems="center">
        <Typography variant="h3" textAlign="center" mb={0.5}>
          {t("page:body.resetYourPassword")}
        </Typography>

        <Typography variant="subtitle1" color="text.tier" textAlign="center">
          {t("page:body.pleaseSetNewPassword")}
        </Typography>
      </Stack>

      <Divider sx={{ mt: 2, mb: 4 }} />

      <Stack>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormInputPassword
            label={t("common:newPassword")}
            validationProps={{
              formId: formId,
              fieldId: "pass1",
              required: true,
            }}
            onChange={(e) => setPass1(e.target?.value)}
          />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormInputPassword
            label={t("field:label.repeatPassword")}
            validationProps={{
              formId: formId,
              fieldId: "pass2",
              required: true,
            }}
            onChange={(e) => setPass2(e.target?.value)}
          />
        </FormControl>

        <Button
          variant="contained"
          fullWidth
          size="large"
          endIcon={<ArrowForwardIcon />}
          disabled={!formValid || !pass1 || !pass2 || pass1 !== pass2}
          onClick={onSubmit}
        >
          {t("button:saveNewPasswordAndLogin")}
        </Button>
      </Stack>
    </LogLayout>
  );
};

export default LoginForgotPasswordReset;
